// Centralized role management for BigSystemOne application

export type UserRole = 'super_admin' | 'system_admin' | 'enterprise_admin' | 'marketing' | 'manager' | 'employee';

export interface UserPermissions {
  canManageUsers: boolean;
  canManageEnterprises: boolean;
  canManageStores: boolean;
  canManageMenuItems: boolean;
  canManageInventory: boolean;
  canViewReports: boolean;
  canManageRoles: boolean;
  canAccessAdmin: boolean;
  canManageAllEnterprises: boolean;
  canManageDiscounts: boolean;
  // Security Audit permissions
  security_audit_view: boolean;
  security_audit_create: boolean;
  security_audit_automated_checks: boolean;
  security_audit_manage_documents: boolean;
}

/**
 * Determine user role based on email and other attributes
 */
/**
 * Extract user's display name from CognitoUser session data
 */
export function getUserDisplayName(user: any, session?: any): string {
  if (!user) return 'User';
  
  // Method 1: Try to get name from session payload
  try {
    if (session?.idToken?.payload) {
      const payload = session.idToken.payload;
      
      // Try given_name + family_name combination
      if (payload.given_name && payload.family_name) {
        return `${payload.given_name} ${payload.family_name}`;
      }
      
      // Try given_name only
      if (payload.given_name) {
        return payload.given_name;
      }
      
      // Try name field
      if (payload.name) {
        return payload.name;
      }
    }
  } catch (e) {
    console.warn('Could not access session IdToken payload for name:', e);
  }
  
  // Method 2: Try to get name from user's attached session
  try {
    if (user.signInUserSession?.idToken?.payload) {
      const payload = user.signInUserSession.idToken.payload;
      
      // Try given_name + family_name combination
      if (payload.given_name && payload.family_name) {
        return `${payload.given_name} ${payload.family_name}`;
      }
      
      // Try given_name only
      if (payload.given_name) {
        return payload.given_name;
      }
      
      // Try name field
      if (payload.name) {
        return payload.name;
      }
    }
  } catch (e) {
    console.warn('Could not access user IdToken payload for name:', e);
  }
  
  // Method 3: Check if this is a plain object with name
  if (user.name && typeof user.name === 'string') {
    return user.name;
  }
  
  // Fallback: Use email if available (remove domain part)
  const email = getUserEmail(user, session);
  if (email) {
    const emailName = email.split('@')[0];
    // Convert email username to a more readable format
    return emailName.replace(/[._-]/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  }
  
  return 'User';
}

/**
 * Extract email from CognitoUser using session data
 */
export function getUserEmail(user: any, session?: any): string {
  if (!user) return '';
  
  // Debug logging - only in development
  if (process.env.NODE_ENV === 'development') {
    console.log('getUserEmail debug:', {
      hasUser: !!user,
      hasSession: !!session,
      sessionType: typeof session,
      sessionMethods: session ? Object.getOwnPropertyNames(session) : [],
      userMethods: user ? Object.getOwnPropertyNames(user) : []
    });
  }
  
  // Method 1: Try to get email from passed session (CognitoUserSession)
  try {
    if (session && typeof session.getIdToken === 'function') {
      const idToken = session.getIdToken();
      if (idToken && typeof idToken.payload === 'object' && idToken.payload.email) {
        return idToken.payload.email;
      }
    }
  } catch (e) {
    console.warn('Could not access session IdToken payload:', e);
  }
  
  // Method 1b: Try legacy session format (backup)
  try {
    if (session?.idToken?.payload?.email) {
      return session.idToken.payload.email;
    }
  } catch (e) {
    console.warn('Could not access legacy session IdToken payload:', e);
  }
  
  // Method 2: Try to get email from user's attached session
  try {
    if (user.signInUserSession?.idToken?.payload?.email) {
      return user.signInUserSession.idToken.payload.email;
    }
  } catch (e) {
    console.warn('Could not access user IdToken payload:', e);
  }
  
  // Method 3: Check if this is a plain object with email
  if (user.email && typeof user.email === 'string') {
    return user.email;
  }
  
  // Method 4: Check if username is an email format
  if (user.username && typeof user.username === 'string' && user.username.includes('@')) {
    return user.username;
  }
  
  // Method 5: Try getUsername and check if it's email format
  if (typeof user.getUsername === 'function') {
    const username = user.getUsername();
    if (username && username.includes('@')) {
      return username;
    }
  }
  
  return '';
}

const ROLE_GROUP_PRIORITY: UserRole[] = [
  'super_admin',
  'system_admin',
  'enterprise_admin',
  'manager',
  'marketing',
  'employee',
];

export function getUserRole(_user: any, session?: any): UserRole {
  const groups = getCognitoGroups(session);
  for (const role of ROLE_GROUP_PRIORITY) {
    if (groups.includes(role)) {
      return role;
    }
  }
  return 'employee';
}

/**
 * Extract Cognito groups from the session ID token
 */
function getCognitoGroups(session: any): string[] {
  try {
    if (!session) return [];
    const idToken = session.getIdToken?.() || session.idToken;
    if (!idToken) return [];
    const payload = idToken.decodePayload?.() || idToken.payload;
    if (!payload) return [];
    return payload['cognito:groups'] || [];
  } catch {
    return [];
  }
}

/**
 * Get user permissions based on role
 */
export function getUserPermissions(role: UserRole): UserPermissions {
  switch (role) {
    case 'super_admin':
      return {
        canManageUsers: true,
        canManageEnterprises: true,
        canManageStores: true,
        canManageMenuItems: true,
        canManageInventory: true,
        canViewReports: true,
        canManageRoles: true,
        canAccessAdmin: true,
        canManageAllEnterprises: true,
        canManageDiscounts: true,
        security_audit_view: true,
        security_audit_create: true,
        security_audit_automated_checks: true,
        security_audit_manage_documents: true,
      };
    
    case 'system_admin':
      return {
        canManageUsers: true,
        canManageEnterprises: true,
        canManageStores: true,
        canManageMenuItems: true,
        canManageInventory: true,
        canViewReports: true,
        canManageRoles: true,
        canAccessAdmin: true,
        canManageAllEnterprises: true,
        canManageDiscounts: true,
        security_audit_view: true,
        security_audit_create: true,
        security_audit_automated_checks: true,
        security_audit_manage_documents: true,
      };
    
    case 'enterprise_admin':
      return {
        canManageUsers: true,
        canManageEnterprises: false,
        canManageStores: true,
        canManageMenuItems: true,
        canManageInventory: true,
        canViewReports: true,
        canManageRoles: false,
        canAccessAdmin: true,
        canManageAllEnterprises: false,
        canManageDiscounts: true,
        security_audit_view: true,
        security_audit_create: true,
        security_audit_automated_checks: true,
        security_audit_manage_documents: true,
      };

    case 'marketing':
      return {
        canManageUsers: false,
        canManageEnterprises: false,
        canManageStores: false,
        canManageMenuItems: false,
        canManageInventory: false,
        canViewReports: true,
        canManageRoles: false,
        canAccessAdmin: false,
        canManageAllEnterprises: false,
        canManageDiscounts: true,
        security_audit_view: false,
        security_audit_create: false,
        security_audit_automated_checks: false,
        security_audit_manage_documents: false,
      };

    case 'manager':
      return {
        canManageUsers: true,
        canManageEnterprises: false,
        canManageStores: false,
        canManageMenuItems: true,
        canManageInventory: true,
        canViewReports: true,
        canManageRoles: false,
        canAccessAdmin: false,
        canManageAllEnterprises: false,
        canManageDiscounts: true,
        security_audit_view: true,
        security_audit_create: false,
        security_audit_automated_checks: true,
        security_audit_manage_documents: false,
      };
    
    case 'employee':
    default:
      return {
        canManageUsers: false,
        canManageEnterprises: false,
        canManageStores: false,
        canManageMenuItems: false,
        canManageInventory: true,
        canViewReports: false,
        canManageRoles: false,
        canAccessAdmin: false,
        canManageAllEnterprises: false,
        canManageDiscounts: false,
        security_audit_view: false,
        security_audit_create: false,
        security_audit_automated_checks: false,
        security_audit_manage_documents: false,
      };
  }
}

/**
 * Check if user has specific permission
 */
export function hasPermission(user: any, permission: keyof UserPermissions, session?: any): boolean {
  const role = getUserRole(user, session);
  const permissions = getUserPermissions(role);
  return permissions[permission];
}

/**
 * Check if user is any level of admin
 */
export function isAdmin(user: any, session?: any): boolean {
  const role = getUserRole(user, session);
  return ['super_admin', 'system_admin', 'enterprise_admin'].indexOf(role) !== -1;
}

/**
 * Check if user is super admin (chris@bigsystemone.com)
 */
export function isSuperAdmin(user: any, session?: any): boolean {
  return getUserRole(user, session) === 'super_admin';
}

/**
 * Check if user can access admin panel
 */
export function canAccessAdmin(user: any, session?: any): boolean {
  return hasPermission(user, 'canAccessAdmin', session);
}